import { useState } from "react";
import { useHistory } from "react-router-dom";
import AuthorIcon from "../../../../assets/icons/user-fill.svg?react";
import { useTranslation } from "react-i18next";
import type { ArticlePageArticle } from "../../hooks/useArticleData";
import { SectionTitle } from "../../../../components/ui/SectionTitle";
import { useApiQuery } from "../../../../services/api";
import { usePageLinks } from "../../../../domains/app/hooks/usePageLinks";

const INITIAL_VISIBLE_COUNT = 3;

interface ArticleSectionAuthorsProps {
  article: ArticlePageArticle;
}

export function ArticleSectionAuthors({ article }: ArticleSectionAuthorsProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { authorLink } = usePageLinks(null);
  const [showAll, setShowAll] = useState(false);

  // Resolved author layer: full list in paper order, with `authorId` set for
  // the ORCID-resolved authors (linkable to their author page). Works without
  // author details return [] — fall back to the legacy display-name strings.
  const authorsQuery = useApiQuery("/webapp/public/articles/:slug/authors", {
    param: { slug: article.slug },
    durable: true,
  });

  // Don't paint the legacy fallback while the resolved list is in flight — it
  // would visibly swap to paper order (and relink) when the query lands. The
  // section simply appears once settled; on error the legacy strings render.
  if (authorsQuery.state === "pending") return null;

  const resolvedAuthors =
    authorsQuery.state === "success" ? authorsQuery.body : [];

  const primaryAuthor = article.primary_author;
  const allAuthors: { name: string; authorId: string | null }[] =
    resolvedAuthors.length > 0
      ? resolvedAuthors
      : primaryAuthor === null || primaryAuthor === ""
        ? []
        : [primaryAuthor, ...article.authors].map((name) => ({
            name,
            authorId: null,
          }));

  if (allAuthors.length === 0) return null;

  const visibleAuthors = showAll
    ? allAuthors
    : allAuthors.slice(0, INITIAL_VISIBLE_COUNT);
  const hasMore = allAuthors.length > INITIAL_VISIBLE_COUNT;

  return (
    <section data-gtm-section="authors" data-gtm-order="10">
      <SectionTitle title={t("Authors")} className="mb-4" />
      <div className="mt-6 mb-2 flex flex-col gap-2">
        {visibleAuthors.map(({ name, authorId }, index) => (
          <div className="flex items-center" key={index}>
            <AuthorIcon className="me-[3px]" />
            {authorId === null ? (
              <span className="text-sm text-primary-black">{name}</span>
            ) : (
              <button
                type="button"
                className="text-sm text-primary-black underline"
                onClick={() => {
                  history.push(authorLink({ id: authorId }));
                }}
              >
                {name}
              </button>
            )}
          </div>
        ))}
      </div>
      {hasMore && !showAll && (
        <button
          type="button"
          className="text-sm text-primary-black underline"
          onClick={() => {
            setShowAll(true);
          }}
        >
          {t("Show all {{count}} authors", { count: allAuthors.length })}
        </button>
      )}
    </section>
  );
}
